import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from 'firebase/messaging';

// Config dari environment variables

// const firebaseConfig = {
//     apiKey: "AIzaSyBCK4KCekO5GJwMAOqV2ihpp6Zh7cjUKFM",
//     authDomain: "billing-sks.firebaseapp.com",
//     projectId: "billing-sks",
//     storageBucket: "billing-sks.firebasestorage.app",
//     messagingSenderId: "268450150408",
//     appId: "1:268450150408:web:50f250c4ecb0206f3679bd",
//     measurementId: "G-TLHDGFP2TS"
// };

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

class FirebaseService {
    constructor() {
        this.app = null;
        this.messaging = null;
        this.isInitialized = false;
        this.vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
        this.serviceWorkerPath = '/firebase-messaging-sw.js';

        // State
        this.currentToken = null;
        this.permission = 'default';
        this.listeners = new Set();
    }

    /**
     * Initialize Firebase dengan error handling
     */
    async initialize() {
        if (this.isInitialized) return true;

        try {
            // Cek apakah browser support
            const supported = await isSupported();
            if (!supported) {
                console.warn('[FIREBASE] Browser tidak mendukung Firebase Messaging');
                return false;
            }

            // Initialize app
            this.app = initializeApp(firebaseConfig);
            this.messaging = getMessaging(this.app);

            this.isInitialized = true;
            console.log('[FIREBASE] Initialized successfully');

            // Check permission status
            this.permission = Notification.permission;

            return true;

        } catch (error) {
            console.error('[FIREBASE] Initialization failed:', error);
            return false;
        }
    }

    /**
     * Register service worker dengan retry mechanism
     */
    async registerServiceWorker() {
        if (!('serviceWorker' in navigator)) {
            throw new Error('Service Worker tidak didukung');
        }

        try {
            // Cek service worker sudah terdaftar
            const existingRegistration = await navigator.serviceWorker.getRegistration(this.serviceWorkerPath);
            if (existingRegistration) {
                console.log('[FIREBASE] Service Worker sudah terdaftar');
                return existingRegistration;
            }

            // Register service worker baru
            const registration = await navigator.serviceWorker.register(this.serviceWorkerPath, {
                scope: '/',
                updateViaCache: 'none'
            });

            console.log('[FIREBASE] Service Worker registered:', registration);

            // Wait for service worker to be ready
            await navigator.serviceWorker.ready;

            return registration;

        } catch (error) {
            console.error('[FIREBASE] Service Worker registration failed:', error);
            throw error;
        }
    }

    /**
     * Request permission dengan user-friendly flow
     */
    async requestPermission() {
        if (!this.isInitialized) {
            await this.initialize();
        }

        // Cek apakah sudah diizinkan
        if (this.permission === 'granted') {
            console.log('[FIREBASE] Permission sudah diberikan');
            return 'granted';
        }

        // Jika permission denied, beri tahu user cara enable manual
        if (this.permission === 'denied') {
            console.log('[FIREBASE] Permission ditolak oleh user');

            // Simpan state untuk ditampilkan di UI
            this.notifyListeners('permission_denied', {});
            return 'denied';
        }

        try {
            // Request permission dengan delay untuk UX
            await new Promise(resolve => setTimeout(resolve, 500));

            const permission = await Notification.requestPermission();
            this.permission = permission;

            console.log(`[FIREBASE] Permission status: ${permission}`);

            if (permission === 'granted') {
                this.notifyListeners('permission_granted', {});
            } else if (permission === 'denied') {
                this.notifyListeners('permission_denied', {});
            }

            return permission;

        } catch (error) {
            console.error('[FIREBASE] Permission request failed:', error);
            this.notifyListeners('error', error);
            return 'default';
        }
    }

    /**
     * Get FCM Token dengan caching
     */
    async getToken() {
        if (!this.isInitialized) {
            const initialized = await this.initialize();
            if (!initialized) return null;
        }

        // Jika sudah ada token, return cached token
        if (this.currentToken) {
            console.log('[FIREBASE] Using cached token');
            return this.currentToken;
        }

        // Cek permission
        if (this.permission !== 'granted') {
            console.log('[FIREBASE] Permission belum diberikan');
            return null;
        }

        try {
            // Register service worker
            const registration = await this.registerServiceWorker();

            // Get token
            const token = await getToken(this.messaging, {
                vapidKey: this.vapidKey,
                serviceWorkerRegistration: registration
            });

            if (token) {
                this.currentToken = token;
                console.log('[FIREBASE] Token received:', token.substring(0, 20) + '...');

                // Simpan ke localStorage untuk recovery
                localStorage.setItem('fcm_token', token);
                localStorage.setItem('fcm_token_timestamp', Date.now().toString());

                this.notifyListeners('token_received', token);
                return token;
            } else {
                console.log('[FIREBASE] No token available');
                return null;
            }

        } catch (error) {
            console.error('[FIREBASE] Get token failed:', error);
            this.notifyListeners('error', error);
            return null;
        }
    }

    /**
     * Setup foreground message listener
     */
    setupForegroundListener() {
        if (!this.isInitialized) return;

        try {
            onMessage(this.messaging, (payload) => {
                console.log('[FIREBASE] Foreground message received:', payload);

                // Notify semua listeners
                this.notifyListeners('message', payload);

                // Play sound jika diperlukan
                this.playNotificationSound();
            });

            console.log('[FIREBASE] Foreground listener setup');

        } catch (error) {
            console.error('[FIREBASE] Setup foreground listener failed:', error);
        }
    }

    /**
     * Play notification sound
     */
    playNotificationSound() {
        try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.3;
            audio.play().catch(e => console.warn('Audio play failed:', e));
        } catch (error) {
            console.warn('[FIREBASE] Notification sound failed:', error);
        }
    }

    /**
     * Clear token dan reset state
     */
    async clearToken() {
        try {
            if (this.currentToken) {
                // Delete token dari Firebase
                await deleteToken(this.messaging);
            }
        } catch (error) {
            console.warn('[FIREBASE] Clear token failed:', error);
        } finally {
            this.currentToken = null;
            localStorage.removeItem('fcm_token');
            localStorage.removeItem('fcm_token_timestamp');

            this.notifyListeners('token_cleared', {});
        }
    }

    /**
     * Check token validity dan refresh jika perlu
     */
    async checkTokenValidity() {
        const savedToken = localStorage.getItem('fcm_token');
        const savedTimestamp = localStorage.getItem('fcm_token_timestamp');

        if (!savedToken || !savedTimestamp) {
            return null;
        }

        // Cek apakah token expired (7 hari)
        const tokenAge = Date.now() - parseInt(savedTimestamp);
        const MAX_TOKEN_AGE = 7 * 24 * 60 * 60 * 1000; // 7 hari

        if (tokenAge > MAX_TOKEN_AGE) {
            console.log('[FIREBASE] Token expired, refreshing...');
            localStorage.removeItem('fcm_token');
            localStorage.removeItem('fcm_token_timestamp');
            return null;
        }

        // Use saved token
        this.currentToken = savedToken;
        return savedToken;
    }

    /**
     * Event listener system
     */
    addListener(event, callback) {
        const listener = { event, callback };
        this.listeners.add(listener);

        return () => {
            this.listeners.delete(listener);
        };
    }

    notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === '*') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('[FIREBASE] Listener error:', error);
                }
            }
        });
    }

    /**
     * Get current state
     */
    getState() {
        return {
            isInitialized: this.isInitialized,
            permission: this.permission,
            hasToken: !!this.currentToken,
            token: this.currentToken,
            isSupported: 'serviceWorker' in navigator && 'PushManager' in window
        };
    }
}

// Singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;